import React, { FC, ReactNode } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import { getHolidayShippingInfo } from './helpers/holidayDeliveryHelpers';

// types
import { PdpSharedShippingHolidayDelivery_sku as Sku } from './__generated__/PdpSharedShippingHolidayDelivery_sku.graphql';
import { PdpSharedShippingHolidayDelivery_item as Item } from './__generated__/PdpSharedShippingHolidayDelivery_item.graphql';
import { PdpSharedShippingHolidayDelivery_viewer as Viewer } from './__generated__/PdpSharedShippingHolidayDelivery_viewer.graphql';

// styles
import styles from './PdpSharedShippingHolidayDelivery.scss';

const PdpSharedShippingHolidayDeliveryModal = React.lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedShippingHolidayDeliveryModal" */ './PdpSharedShippingHolidayDeliveryModal'
        )
);

const PdpSharedShippingHolidayDeliveryTooltip = React.lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedShippingHolidayDeliveryTooltip" */ './PdpSharedShippingHolidayDeliveryTooltip'
        )
);

const BowIcon = React.lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedShippingHolidayDeliveryBowIcon" */ 'dibs-icons/exports/Bow'
        )
);

type Props = {
    sku: Sku;
    item: Item;
    viewer: Viewer;
    isClient: boolean;
    useIcon?: boolean;
    children: ReactNode;
    useTooltipDetails?: boolean;
};

export const PdpSharedShippingHolidayDeliveryComponent: FC<Props> = ({
    sku,
    item,
    viewer,
    useIcon,
    isClient,
    useTooltipDetails,
}) => {
    const { relevantShipping, transactionWarnings } = viewer || {};
    const { hasRestrictedMaterials, isCheckoutBlocked } = transactionWarnings || {};
    const shipmentDeliveryEstimations = sku
        ? sku?.shipmentDeliveryEstimations
        : item?.shipmentDeliveryEstimations;
    const holidayShippingMethods = getHolidayShippingInfo({
        shipmentDeliveryEstimations,
        shipmentQuotes: relevantShipping?.shipmentQuotes || [],
    });

    if (!holidayShippingMethods.length || hasRestrictedMaterials || isCheckoutBlocked) {
        return null;
    }

    const {
        totalAmount,
        isComplimentary,
        estimatedDeliveryDate,
        buyerServiceMethodDisplay,
    } = holidayShippingMethods[0];

    const isFreeShipping = isComplimentary || totalAmount?.amount === 0;
    return (
        <div className={styles.container}>
            {useIcon && isClient && (
                <React.Suspense fallback={null}>
                    <BowIcon className={styles.iconComponent} />
                </React.Suspense>
            )}
            <span className={styles.text}>
                <FormattedMessage
                    id="pdp.sharedHolidayShipping.estimatedDelivery"
                    defaultMessage="<b>Get it in time for the holidays:</b>{isFreeShipping, select, 
                        true { Free}
                        other {}
                        } {buyerServiceMethodDisplay} Shipping arrives by {estimatedDeliveryDate}."
                    values={{
                        isFreeShipping,
                        buyerServiceMethodDisplay,
                        b: (...chunks: ReactNode[]) => (
                            <span className={styles.boldText}>{chunks}</span>
                        ),
                        estimatedDeliveryDate: (
                            <FormattedDate
                                value={estimatedDeliveryDate}
                                month="short"
                                day="2-digit"
                            />
                        ),
                    }}
                />
                {useTooltipDetails && isClient && (
                    <React.Suspense fallback={null}>
                        <PdpSharedShippingHolidayDeliveryTooltip />
                    </React.Suspense>
                )}
                {!useTooltipDetails && isClient && (
                    <React.Suspense fallback={null}>
                        <PdpSharedShippingHolidayDeliveryModal />
                    </React.Suspense>
                )}
            </span>
        </div>
    );
};

export const PdpSharedShippingHolidayDelivery = createFragmentContainer(
    PdpSharedShippingHolidayDeliveryComponent,
    {
        viewer: graphql`
            fragment PdpSharedShippingHolidayDelivery_viewer on Viewer {
                transactionWarnings(itemId: $itemId, country: $userCountryCode)
                @include(if: $isClient) {
                    hasRestrictedMaterials
                    isCheckoutBlocked
                }
                relevantShipping(itemId: $itemId, buyerId: $userId) @include(if: $isClient) {
                    shipmentQuotes {
                        serviceId
                        isComplimentary
                        buyerServiceMethodDisplay
                        totalAmount {
                            amount
                        }
                        serviceMethod {
                            serviceType
                            serviceMethodType
                            region {
                                code
                            }
                        }
                    }
                }
            }
        `,
        item: graphql`
            fragment PdpSharedShippingHolidayDelivery_item on Item {
                shipmentDeliveryEstimations {
                    shipmentQuoteId
                    maxDaysToDeliver
                }
            }
        `,
        sku: graphql`
            fragment PdpSharedShippingHolidayDelivery_sku on ItemSkuType {
                shipmentDeliveryEstimations {
                    shipmentQuoteId
                    maxDaysToDeliver
                }
            }
        `,
    }
);
